import { useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { ParsedQs, stringify } from "qs";
import { useSelector } from "react-redux";

import { AvailableFilter } from "api/requests/getProductsV3";
import { Query } from "custom-types/Query";
import { clearKeysFromQuery } from "hooks/useClearQueryStringKeys";
import { getIsDelta9Allowed } from "redux/selectors/complianceRules";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import FilterShortcut from "components/botanic/FilterShortcut";
import { InformationalModal } from "components/botanic/Modal";
import { FilterButton } from "components/FilteredGrid/FilterButton";
import { MenuFilterGroupTypes } from "components/MenuFilters/types/MenuFilters";

import {
  getUpdatedFilterHref,
  getUpdatedFulfillmentHref,
} from "../utils/filterUtils";

export const DELTA9_UNRESTRICTED_FULFILLMENT_METHODS = [
  { name: "Pickup", value: "pickup" },
  { name: "Delivery", value: "delivery" },
];

export const DELTA9_RESTRICTED_FULFILLMENT_METHODS = [
  { name: "Direct Shipping", value: "shipping" },
  { name: "Pickup", value: "pickup" },
  { name: "Delivery", value: "delivery" },
];

const MobileFilterModalAndShortcutsUI = ({
  availableFilters,
  filters,
  chips,
  setOpenFilterSection,
}: {
  availableFilters: AvailableFilter[];
  filters: React.ReactNode;
  chips: React.ReactNode;
  setOpenFilterSection?: (filterName?: string) => void;
}) => {
  const router = useRouter();
  const { asPath, push } = router;
  const { path, query } = parseNextRouterAsPath<ParsedQs>(asPath);

  const [modalActive, setModalActive] = useState(false);
  const modalButtonRef = useRef(null);

  const isDelta9Allowed = useSelector(getIsDelta9Allowed);

  const filtersApplied = query.filter as Query;
  const fulfillmentsApplied = query.fulfillment;
  const hasFiltersOrFulfillmentsApplied =
    !!filtersApplied || !!fulfillmentsApplied;

  const numberOfFiltersSelected = Object.entries(filtersApplied || {}).length;

  const clearOpenSection = () => {
    setOpenFilterSection && setOpenFilterSection(undefined);
  };

  return (
    <div className="block relative md:hidden">
      <>
        <div className="flex items-center overflow-x-auto overflow-y-hidden whitespace-nowrap pb-sm mb-xs gap-1 no-scrollbar fade-white-right">
          <FilterButton
            filterCount={numberOfFiltersSelected}
            onClick={() => {
              setModalActive(true);
            }}
            ref={modalButtonRef}
          />

          {chips}
          {/* TODO This could use some cleanup, but requires some modal refactoring to do so. */}
          {!hasFiltersOrFulfillmentsApplied && (
            <>
              {isDelta9Allowed
                ? DELTA9_UNRESTRICTED_FULFILLMENT_METHODS.map(
                    (fulfillment: { name: string; value: string }) => (
                      <MobileMenuQuickFilter
                        key={fulfillment.value}
                        text={fulfillment.name}
                        type={MenuFilterGroupTypes.toggle}
                        onClick={() =>
                          push(
                            getUpdatedFulfillmentHref(
                              asPath,
                              fulfillment.value,
                            ),
                          )
                        }
                      />
                    ),
                  )
                : DELTA9_RESTRICTED_FULFILLMENT_METHODS.map(
                    (fulfillment: { name: string; value: string }) => (
                      <MobileMenuQuickFilter
                        key={fulfillment.value}
                        text={fulfillment.name}
                        type={MenuFilterGroupTypes.toggle}
                        onClick={() =>
                          push(
                            getUpdatedFulfillmentHref(
                              asPath,
                              fulfillment.value,
                            ),
                          )
                        }
                      />
                    ),
                  )}
              {availableFilters.length > 0
                ? availableFilters.map((filter) => (
                    <MobileMenuQuickFilter
                      key={filter.name}
                      text={filter.label}
                      type={filter.type as unknown as MenuFilterGroupTypes}
                      onClick={() => {
                        if (filter.type === "toggle") {
                          push(
                            getUpdatedFilterHref(asPath, filter.name, "true"),
                          );
                        } else {
                          setOpenFilterSection &&
                            setOpenFilterSection(filter.name);
                          setModalActive(true);
                        }
                      }}
                    />
                  ))
                : null}
            </>
          )}
        </div>
        {modalActive && (
          <InformationalModal
            title={`Filters${
              numberOfFiltersSelected ? ` (${numberOfFiltersSelected})` : ""
            }`}
            returnFocusRef={modalButtonRef}
            onDismissed={() => {
              setModalActive(false);
              clearOpenSection();
            }}
            expository
          >
            <div className="relative -top-4 -mx-lg h-[calc(100%+32px)] flex flex-col justify-between">
              <div>{filters}</div>
              <div className="sticky -bottom-4 flex w-full p-lg bg-white border-t border-light-grey">
                <Link
                  href={{
                    pathname: path,
                    query: stringify(
                      clearKeysFromQuery(query, [
                        "filter",
                        "fulfillment",
                        "page",
                        "q",
                        "sort",
                      ]),
                    ),
                  }}
                  className="button button--text flex-shrink-0 px-xxl text-xs text-default leading-[34px]"
                >
                  clear all
                </Link>
                <button
                  className="button button--secondary"
                  onClick={() => {
                    setModalActive(false);
                    clearOpenSection();
                  }}
                >
                  confirm
                </button>
              </div>
            </div>
          </InformationalModal>
        )}
      </>
    </div>
  );
};

const MobileMenuQuickFilter: React.FC<{
  onClick: () => void;
  text: string;
  type: MenuFilterGroupTypes;
}> = ({ onClick, text, type }) => {
  const isToggleFilter = type === MenuFilterGroupTypes.toggle;

  return (
    <FilterShortcut
      showChevron={!isToggleFilter}
      text={text}
      onClick={onClick}
    />
  );
};

export default MobileFilterModalAndShortcutsUI;
